import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Wallet, Clock, Users, Send, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_CONFIG = {
  NEW:            { label: 'Nouveau',      color: '#94A3B8' },
  PENDING_REVIEW: { label: 'En attente',   color: '#E8920A' },
  ELIGIBLE:       { label: 'Éligible',     color: '#3B82F6' },
  IN_PROGRESS:    { label: 'En cours',     color: '#8B5CF6' },
  NEGOTIATING:    { label: 'Négociation',  color: '#F59E0B' },
  WON:            { label: 'Gagné',        color: '#0D9F57' },
  PAID:           { label: 'Payé',         color: '#059669' },
  LOST:           { label: 'Perdu',        color: '#DC2626' },
  INELIGIBLE:     { label: 'Non éligible', color: '#6B7280' },
};

function KPI({ label, value, sub, icon: Icon, color = '#0D1B3E' }) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ ...styles.kpiIcon, background: `${color}15` }}>
        <Icon size={20} color={color} />
      </div>
      <div style={styles.kpiValue}>{value}</div>
      <div style={styles.kpiLabel}>{label}</div>
      {sub && <div style={styles.kpiSub}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [followup, setFollowup] = useState([]);
  const [replyMsg, setReplyMsg] = useState({});
  const [sending, setSending] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [statsR, casesR, followupR] = await Promise.all([
          axios.get(`${API}/api/dashboard`),
          axios.get(`${API}/api/cases?limit=5`),
          axios.get(`${API}/api/inbox/active`),
        ]);
        setStats(statsR.data);
        setCases(casesR.data);
        setFollowup(followupR.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const sendFollowup = async (phone) => {
    if (!replyMsg[phone]?.trim()) return;
    setSending(s => ({ ...s, [phone]: true }));
    try {
      await axios.post(`${API}/api/followup/${phone}`, { message: replyMsg[phone] });
      setReplyMsg(r => ({ ...r, [phone]: '' }));
      // Refresh chat messages in real time if modal is open
      if (selectedChat?.client_phone === phone) {
        const r2 = await axios.get(`${API}/api/inbox/${selectedChat.id}/messages`);
        setChatMessages(r2.data);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      }
      setFollowup(f => f.filter(c => c.client_phone !== phone));
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setSending(s => ({ ...s, [phone]: false })); }
  };

  const unlock = async (phone) => {
    if (!window.confirm('Débloquer cette conversation ?')) return;
    try {
      await axios.post(`${API}/api/conversations/${phone}/unlock`);
      setFollowup(f => f.map(c => c.client_phone === phone ? {...c, state:'COMPLETE'} : c));
      if (selectedChat?.client_phone === phone) setSelectedChat(prev => ({...prev, state:'COMPLETE'}));
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const loadChat = async (conv) => {
    setSelectedChat(conv);
    setLoadingChat(true);
    try {
      const r = await axios.get(`${API}/api/inbox/${conv.id}/messages`);
      setChatMessages(r.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    } catch(e) { console.error(e); }
    finally { setLoadingChat(false); }
  };

  if (loading || !stats) return <div style={styles.loading}>Chargement...</div>;

  // Pie chart data
  const pieData = Object.entries(STATUS_CONFIG)
    .map(([key, { label, color }]) => ({ name: label, value: stats[key.toLowerCase()] || 0, color }))
    .filter(d => d.value > 0);

  // Revenue bar data (mock monthly from stats)
  const revenueData = [
    { month: 'Jan', val: 0 }, { month: 'Fév', val: 0 }, { month: 'Mar', val: 0 },
    { month: 'Avr', val: 0 }, { month: 'Mai', val: 0 }, { month: 'Jun', val: parseFloat(stats.total_revenue || 0) },
  ];

  const convRate = stats.total > 0 ? Math.round(((stats.won || 0) + (stats.paid || 0)) / stats.total * 100) : 0;

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard</h1>
          <p style={styles.sub}>{stats.total} dossier{stats.total !== 1 ? 's' : ''} · {stats.new_this_week || 0} cette semaine
            {stats.mandates_signed > 0 && ` · ${stats.mandates_signed} mandat${stats.mandates_signed > 1 ? 's' : ''} signé${stats.mandates_signed > 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={() => navigate('/cases/new')} style={styles.newBtn}>
          + Nouveau dossier
        </button>
      </div>

      {/* KPIs */}
      <div style={styles.kpiGrid}>
        <KPI label="Revenus FlyCash" value={`${parseFloat(stats.total_revenue || 0).toFixed(0)}€`} sub="honoraires cumulés" icon={TrendingUp} color="#0D9F57" />
        <KPI label="Versé aux clients" value={`${parseFloat(stats.total_paid_clients || 0).toFixed(0)}€`} sub="indemnisations payées" icon={Wallet} color="#3B82F6" />
        <KPI label="Taux de succès" value={`${convRate}%`} sub="dossiers gagnés / total" icon={CheckCircle} color="#E8920A" />
        <KPI label="Dossiers actifs" value={(stats.eligible || 0) + (stats.in_progress || 0) + (stats.negotiating || 0)} sub="en cours de traitement" icon={Clock} color="#8B5CF6" />
      </div>

      <div style={styles.grid2}>
        {/* Status breakdown */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Répartition des dossiers</h3>
          {stats.total === 0 ? (
            <p style={styles.empty}>Aucun dossier</p>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" strokeWidth={0}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1 }}>
                {Object.entries(STATUS_CONFIG).map(([key, { label, color }]) => {
                  const val = stats[key.toLowerCase()] || 0;
                  if (!val) return null;
                  return (
                    <div key={key} style={styles.statusRow}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={styles.statusLabel}>{label}</span>
                      <span style={styles.statusVal}>{val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Revenue chart */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Revenus mensuels (€)</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={revenueData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={35} />
              <Tooltip formatter={(v) => [`${v}€`, 'Revenus']} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
              <Bar dataKey="val" fill="#E8920A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={styles.grid2}>
        {/* Recent cases */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Dossiers récents</h3>
            <button onClick={() => navigate('/cases')} style={styles.seeAll}>Voir tout <ArrowRight size={12} /></button>
          </div>
          {cases.length === 0 ? (
            <p style={styles.empty}>Aucun dossier</p>
          ) : cases.map(c => {
            const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.PENDING_REVIEW;
            return (
              <div key={c.id} onClick={() => navigate(`/cases/${c.id}`)} style={styles.caseRow}>
                <div style={styles.caseInfo}>
                  <div style={styles.caseName}>{c.client_name || c.client_phone}</div>
                  <div style={styles.caseFlight}>{c.flight_number || 'Vol N/A'} · {c.departure_airport || '?'} → {c.arrival_airport || '?'}</div>
                </div>
                <div style={styles.caseRight}>
                  <span style={{ ...styles.statusBadge, background: `${sc.color}18`, color: sc.color }}>{sc.label}</span>
                  {c.total_compensation && <span style={styles.caseAmount}>{parseFloat(c.total_compensation).toFixed(0)}€</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Follow-up widget */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <AlertCircle size={15} color="#E8920A" style={{ marginRight: 6 }} />
              Chats en cours
            </h3>
            <span style={styles.followupCount}>{followup.length}</span>
          </div>
          {followup.length === 0 ? (
            <div style={styles.emptyFollowup}>
              <CheckCircle size={32} color="#0D9F57" />
              <p>Aucune conversation en cours</p>
            </div>
          ) : followup.map(conv => (
            <div key={conv.id} style={{...styles.followupItem, cursor:'pointer'}}
              onClick={() => loadChat(conv)}>
              <div style={styles.followupInfo}>
                <div style={{...styles.followupName, display:'flex', justifyContent:'space-between'}}>
                  <span>{conv.client_name || conv.client_phone}</span>
                  <span style={{fontSize:11, color:'#9CA3AF', fontWeight:400}}>
                    {conv.last_message_at && new Date(conv.last_message_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
                  </span>
                </div>
                <div style={styles.followupDetail}>
                  {conv.flight_number ? `Vol ${conv.flight_number}` : conv.client_phone}
                  {' · '}
                  {conv.state?.replace('COLLECTING_','').replace(/_/g,' ').toLowerCase()}
                </div>
                {conv.last_message && (
                  <div style={styles.followupLast}>
                    {conv.last_message_direction === 'outbound' ? '→ ' : '← '}
                    {conv.last_message.substring(0, 70)}{conv.last_message.length > 70 ? '...' : ''}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Chat modal */}
          {selectedChat && (
            <div style={styles.chatModal} onClick={e => e.target === e.currentTarget && setSelectedChat(null)}>
              <div style={styles.chatModalInner}>
                <div style={styles.chatModalHeader}>
                  <div>
                    <div style={{fontWeight:800, color:'#0D1B3E'}}>
                      {selectedChat.client_name || selectedChat.client_phone}
                    </div>
                    <div style={{fontSize:12, color:'#9CA3AF'}}>
                      {selectedChat.client_phone} · {selectedChat.state}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    {selectedChat?.state === 'NEEDS_HUMAN' && (
                      <button onClick={() => unlock(selectedChat.client_phone)}
                        style={{background:'#DC2626',color:'#fff',border:'none',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                        Débloquer
                      </button>
                    )}
                    <button onClick={() => setSelectedChat(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#9CA3AF'}}>✕</button>
                  </div>
                </div>
                <div style={styles.chatModalMessages}>
                  {loadingChat ? (
                    <p style={{textAlign:'center',color:'#9CA3AF',padding:20}}>Chargement...</p>
                  ) : chatMessages.map(msg => (
                    <div key={msg.id} style={{display:'flex', justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start', marginBottom:8}}>
                      <div style={{
                        maxWidth:'75%', padding:'8px 12px', borderRadius:12,
                        background: msg.direction === 'outbound' ? '#0D1B3E' : '#F3F4F6',
                        color: msg.direction === 'outbound' ? '#fff' : '#1F2937',
                        fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap', wordBreak:'break-word'
                      }}>
                        {msg.content}
                        <div style={{fontSize:10, opacity:0.6, marginTop:4, textAlign:'right'}}>
                          {new Date(msg.created_at).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div style={styles.chatModalReply}>
                  <textarea
                    value={replyMsg[selectedChat.client_phone] || ''}
                    onChange={e => setReplyMsg(r => ({...r, [selectedChat.client_phone]: e.target.value}))}
                    placeholder="Écrire un message..."
                    style={{...styles.followupInput, height:50, flex:1}}
                    onKeyDown={e => { if ((e.metaKey||e.ctrlKey) && e.key==='Enter') sendFollowup(selectedChat.client_phone); }}
                  />
                  <button
                    onClick={() => sendFollowup(selectedChat.client_phone)}
                    disabled={sending[selectedChat.client_phone] || !replyMsg[selectedChat.client_phone]?.trim()}
                    style={{...styles.followupBtn, padding:'10px 16px'}}
                  >
                    <Send size={15} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats row: top airlines, top destinations, avg delay */}
      <div style={{...styles.grid2, gridTemplateColumns: '1fr 1fr 1fr', marginBottom: 20}}>
        {/* Top 5 airlines */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Top compagnies</h3>
          {(!stats.top_airlines?.length) ? (
            <p style={styles.empty}>Aucune donnée</p>
          ) : stats.top_airlines.map((a, i) => (
            <div key={a.airline} style={styles.rankRow}>
              <span style={styles.rankNum}>{i+1}</span>
              <span style={styles.rankName}>{a.airline}</span>
              <span style={styles.rankVal}>{a.count} dossier{a.count > 1 ? 's' : ''}</span>
            </div>
          ))}
        </div>

        {/* Top 5 destinations */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Top destinations</h3>
          {(!stats.top_destinations?.length) ? (
            <p style={styles.empty}>Aucune donnée</p>
          ) : stats.top_destinations.map((d, i) => (
            <div key={d.destination} style={styles.rankRow}>
              <span style={styles.rankNum}>{i+1}</span>
              <span style={styles.rankName}>{d.destination}</span>
              <span style={styles.rankVal}>{d.count} dossier{d.count > 1 ? 's' : ''}</span>
            </div>
          ))}
        </div>

        {/* Avg delay per airline */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Retard moyen par compagnie</h3>
          {(!stats.top_airlines?.length) ? (
            <p style={styles.empty}>Aucune donnée</p>
          ) : stats.top_airlines.map((a, i) => (
            <div key={a.airline} style={styles.rankRow}>
              <span style={styles.rankNum}>{i+1}</span>
              <span style={styles.rankName}>{a.airline}</span>
              <span style={{...styles.rankVal, color: '#E8920A', fontWeight: 700}}>
                {a.avg_delay ? `${parseFloat(a.avg_delay).toFixed(1)}h` : '—'}
              </span>
            </div>
          ))}
          {stats.avg_delay_hours > 0 && (
            <div style={{marginTop: 12, paddingTop: 8, borderTop: '1px solid #F3F4F6', fontSize: 12, color: '#9CA3AF'}}>
              Moyenne globale : {stats.avg_delay_hours}h
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

const styles = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 },
  title: { fontSize: 26, fontWeight: 800, color: '#0D1B3E', margin: 0 },
  sub: { fontSize: 13, color: '#9CA3AF', marginTop: 4 },
  newBtn: { background: '#0D1B3E', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 },
  kpiCard: { background: '#fff', borderRadius: 14, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 4 },
  kpiIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  kpiValue: { fontSize: 28, fontWeight: 800, color: '#0D1B3E', lineHeight: 1 },
  kpiLabel: { fontSize: 13, fontWeight: 600, color: '#374151' },
  kpiSub: { fontSize: 11, color: '#9CA3AF' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
  card: { background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#0D1B3E', margin: 0, display: 'flex', alignItems: 'center' },
  seeAll: { fontSize: 12, color: '#E8920A', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 },
  statusRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #F9FAFB' },
  statusLabel: { flex: 1, fontSize: 13, color: '#374151' },
  statusVal: { fontSize: 13, fontWeight: 700, color: '#0D1B3E' },
  caseRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F9FAFB', cursor: 'pointer' },
  caseInfo: {},
  caseName: { fontSize: 13, fontWeight: 700, color: '#0D1B3E' },
  caseFlight: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },
  caseRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  statusBadge: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 100 },
  caseAmount: { fontSize: 12, fontWeight: 700, color: '#0D9F57' },
  followupCount: { background: '#FEF3C7', color: '#D97706', fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 100 },
  followupItem: { padding: '12px 0', borderBottom: '1px solid #F9FAFB' },
  followupInfo: { marginBottom: 8 },
  followupName: { fontSize: 13, fontWeight: 700, color: '#0D1B3E' },
  followupDetail: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },
  followupLast: { fontSize: 11, color: '#CBD5E1', marginTop: 2, fontStyle: 'italic' },
  followupReply: { display: 'flex', gap: 8 },
  followupInput: { flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12, fontFamily: 'inherit', outline: 'none', resize: 'none', height: 36 },
  followupBtn: { background: '#E8920A', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' },
  emptyFollowup: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '20px 0', color: '#9CA3AF', fontSize: 13 },
  empty: { fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '20px 0' },
  loading: { padding: 40, textAlign: 'center', color: '#9CA3AF' },
  rankRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F9FAFB' },
  rankNum: { width: 20, height: 20, background: '#F3F4F6', borderRadius: '50%', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', flexShrink: 0 },
  rankName: { flex: 1, fontSize: 13, color: '#374151', fontWeight: 600 },
  rankVal: { fontSize: 12, color: '#9CA3AF' },
  chatModal: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' },
  chatModalInner: { background:'#fff', borderRadius:16, width:'90%', maxWidth:580, height:'75vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' },
  chatModalHeader: { padding:'16px 20px', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center' },
  chatModalMessages: { flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:4 },
  chatModalReply: { padding:'12px 16px', borderTop:'1px solid #E5E7EB', display:'flex', gap:10, alignItems:'flex-end' },
};
